import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './MultiSelectFilter.module.css';

// Dropdown filter whose menu is a checklist, so several options can be ticked
// at once (e.g. the Age filter on the athlete lists, where a club that groups
// U8 and U9 into one age range wants to see both). Selection semantics:
//   - `value` is the array of ticked option values. EMPTY means "no filter";
//     the trigger then shows `allLabel`, and the top row of the menu (also
//     labelled `allLabel`) re-selects that state.
//   - Ticking applies immediately; there is no OK/Cancel, so unlike a modal
//     dialog the menu closes on an outside click (same as CustomDropdown).
// The trigger is styled to sit flush with the native <select>s in the admin
// filter cards (bulkAdminTable.module.css .filterSelect).

// Up to this many ticked labels are listed on the trigger; beyond it a count
// ("4 ages") keeps the control from growing past its neighbours.
const MAX_INLINE_LABELS = 3;

const MultiSelectFilter = ({
  value,
  options,
  onChange,
  allLabel,
  itemLabel = 'item',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  // Close on outside click — the listener only lives while the menu is open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleMouseDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  const selected = useMemo(() => new Set(value), [value]);

  // Trigger text: the ticked labels in option order (not click order), or a
  // count once there are too many to fit.
  const selectedOptions = options.filter((o) => selected.has(o.value));
  let triggerText = allLabel;
  if (selectedOptions.length > MAX_INLINE_LABELS) {
    triggerText = `${selectedOptions.length} ${itemLabel}s`;
  } else if (selectedOptions.length > 0) {
    triggerText = selectedOptions.map((o) => o.label).join(', ');
  }

  const toggle = (optionValue) => {
    onChange(
      selected.has(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue]
    );
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && isOpen) {
      event.stopPropagation();
      setIsOpen(false);
      if (triggerRef.current) triggerRef.current.focus();
    }
  };

  return (
    <div className={styles.container} ref={containerRef} onKeyDown={handleKeyDown}>
      <button
        type="button"
        ref={triggerRef}
        className={styles.trigger}
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span className={styles.triggerText}>{triggerText}</span>
        <svg
          className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
          width="12"
          height="8"
          viewBox="0 0 12 8"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M1 1.5L6 6.5L11 1.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {isOpen && (
        <div className={styles.menu}>
          <label className={`${styles.option} ${styles.optionAll}`}>
            <input
              type="checkbox"
              checked={value.length === 0}
              onChange={() => onChange([])}
            />
            <span>{allLabel}</span>
          </label>
          {options.map((o) => (
            <label key={o.value} className={styles.option}>
              <input
                type="checkbox"
                checked={selected.has(o.value)}
                onChange={() => toggle(o.value)}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

MultiSelectFilter.propTypes = {
  // Ticked option values; empty = no filter.
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  // Receives the full next array of ticked values.
  onChange: PropTypes.func.isRequired,
  // Shown on the trigger and the top menu row when nothing is ticked ("All ages").
  allLabel: PropTypes.string.isRequired,
  // Singular noun for the "N items" trigger text ("age" → "4 ages").
  itemLabel: PropTypes.string,
};

export default MultiSelectFilter;
